import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Computer, 
  ShoppingCart, 
  Star, 
  Eye, 
  ArrowLeft,
  Cpu, 
  HardDrive, 
  Monitor,
  Zap,
  MemoryStick,
  Fan,
  Box,
  Share2,
  CircuitBoard,
  ChevronDown
} from 'lucide-react';
import { PcConfigService, PcConfiguration } from '../services/pcConfigService';
import { ProductSupplier } from '../services/api/api.types';
import { useCart } from '../contexts/CartContext';
import Layout from '../components/layout/Layout';
import logger from '../utils/logger';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { config } from '../config/env';
import { splitDescriptionSpecs } from '../utils/descriptionSpecs';

const SPEC_ICONS: [string, React.ReactNode][] = [
  ['охладител', <Fan className="h-6 w-6" />],
  ['процесор', <Cpu className="h-6 w-6" />],
  ['дънна', <CircuitBoard className="h-6 w-6" />],
  ['памет', <MemoryStick className="h-6 w-6" />],
  ['видеокарта', <Monitor className="h-6 w-6" />],
  ['захранване', <Zap className="h-6 w-6" />],
  ['кутия', <Box className="h-6 w-6" />],
  ['ssd', <HardDrive className="h-6 w-6" />],
  ['hdd', <HardDrive className="h-6 w-6" />]
];

const getSpecIcon = (label: string): React.ReactNode =>
  SPEC_ICONS.find(([keyword]) => label.toLowerCase().includes(keyword))?.[1] ?? <Computer className="h-6 w-6" />;

// Component to handle PC case image loading
const PcCaseImage: React.FC<{ pcCaseId?: number | string; pcCaseSupplier?: ProductSupplier | null; configName: string; customImageUrl?: string; customImageUrls?: string[] }> = ({ pcCaseId, pcCaseSupplier, configName, customImageUrl, customImageUrls }) => {
  const [images, setImages] = useState<string[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoading, setImageLoading] = useState(false);

  useEffect(() => {
    const fetchPcCaseImages = async () => {
      const baseUrl = config.api.baseUrl;

      // If there are custom images (for used configs), use them
      if (customImageUrls && customImageUrls.length > 0) {
        const fullUrls = customImageUrls.map(url => `${baseUrl}${url}`);
        setImages(fullUrls);
        setImageLoading(false);
      }
      // Fallback to old single image field
      else if (customImageUrl) {
        setImages([`${baseUrl}${customImageUrl}`]);
        setImageLoading(false);
      }
      // Otherwise fetch PC case images
      else if (pcCaseId) {
        setImageLoading(true);
        try {
          const urls = await PcConfigService.getPcCaseImages(pcCaseId, pcCaseSupplier);
          setImages(urls);
        } catch (error) {
          logger.error('Failed to fetch PC case images:', error);
          setImages([]);
        } finally {
          setImageLoading(false);
        }
      } else {
        setImages([]);
        setImageLoading(false);
      }
    };

    fetchPcCaseImages();
  }, [pcCaseId, pcCaseSupplier, customImageUrl, customImageUrls]);

  if (!pcCaseId && !customImageUrl && (!customImageUrls || customImageUrls.length === 0)) {
    return (
      <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center border border-gray-300">
        <div className="text-center text-gray-500">
          <Computer className="h-20 w-20 mx-auto mb-4" />
          <p className="text-lg">Gaming PC</p>
        </div>
      </div>
    );
  }

  if (imageLoading) {
    return (
      <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center border border-gray-300">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Зареждане...</p>
        </div>
      </div>
    );
  }

  if (images.length === 0 && !imageLoading) {
    return (
      <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center border border-gray-300">
        <div className="text-center text-gray-500">
          <Computer className="h-20 w-20 mx-auto mb-4" />
          <p className="text-lg">Gaming PC</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Main Image Display */}
      <div className="aspect-square bg-gray-100 rounded-2xl overflow-hidden border border-gray-300 relative group">
        {images.length > 0 && (
          <>
            <img
              src={images[currentImageIndex]}
              alt={configName}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              onError={(e) => {
                const parent = e.currentTarget.parentElement!;
                parent.innerHTML = `
                  <div class="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                    <div class="text-center text-gray-500">
                      <div class="text-4xl mb-2">🖼️</div>
                      <p>Изображението не може да бъде заредено</p>
                    </div>
                  </div>
                `;
              }}
            />
            {/* Navigation Arrows */}
            {images.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-all opacity-0 group-hover:opacity-100"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-all opacity-0 group-hover:opacity-100"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}
          </>
        )}
      </div>

      {/* Thumbnail Gallery */}
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                index === currentImageIndex
                  ? 'border-blue-500 shadow-lg'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <img
                src={image}
                alt={`${configName} - Image ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const toEUR = (eur: number) => Number(eur).toFixed(2);

const PCConfigDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [configuration, setConfiguration] = useState<PcConfiguration | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [selectedUpgrades, setSelectedUpgrades] = useState<{ [key: string]: any }>({});
  const { addToCart, cart } = useCart();
  useDocumentTitle(configuration?.name);

  useEffect(() => {
    const fetchConfiguration = async () => {
      if (!id) {
        setError('Невалиден ID на конфигурация');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        const config = await PcConfigService.getPublishedConfig(Number(id));
        setConfiguration(config);
      } catch (err) {
        logger.error('Failed to fetch configuration:', err);
        setError('Конфигурацията не е намерена или е недостъпна');
      } finally {
        setLoading(false);
      }
    };

    fetchConfiguration();
  }, [id]);

  // Handle upgrade selection
  const handleUpgradeSelect = (componentType: string, upgrade: any) => {
    setSelectedUpgrades(prev => ({
      ...prev,
      [componentType]: upgrade
    }));
  };

  // Calculate upgrade price difference
  const calculateUpgradePrice = () => {
    let upgradeTotal = 0;
    
    Object.entries(selectedUpgrades).forEach(([componentType, upgrade]) => {
      if (upgrade) {
        // Find base component price
        const basePrice = getBaseComponentPrice(componentType);
        const upgradePrice = parseFloat(upgrade.price?.toString() || '0') || 0;
        upgradeTotal += (upgradePrice - basePrice);
      }
    });
    
    return upgradeTotal;
  };

  const getBaseComponentPrice = (componentType: string) => {
    if (!configuration) return 0;
    
    switch (componentType) {
      case 'gpu': return parseFloat(configuration.gpuPrice?.toString() || '0') || 0;
      case 'cpu': return parseFloat(configuration.cpuPrice?.toString() || '0') || 0;
      case 'ram': return parseFloat(configuration.ramPrice?.toString() || '0') || 0;
      case 'motherboard': return parseFloat(configuration.motherboardPrice?.toString() || '0') || 0;
      case 'ssd': return parseFloat(configuration.ssdPrice?.toString() || '0') || 0;
      case 'hdd': return parseFloat(configuration.hddPrice?.toString() || '0') || 0;
      case 'pcCase': return parseFloat(configuration.pcCasePrice?.toString() || '0') || 0;
      case 'cpuCooler': return parseFloat(configuration.cpuCoolerPrice?.toString() || '0') || 0;
      case 'powerSupply': return parseFloat(configuration.powerSupplyPrice?.toString() || '0') || 0;
      default: return 0;
    }
  };

  // Get component key from label
  const getComponentKey = (label: string): string => {
    switch (label.toLowerCase()) {
      case 'видеокарта (gpu)': return 'gpu';
      case 'процесор (cpu)': return 'cpu';
      case 'памет (ram)': return 'ram';
      case 'дънна платка': return 'motherboard';
      case 'ssd съхранение': return 'ssd';
      case 'hdd съхранение': return 'hdd';
      case 'pc кутия': return 'pcCase';
      case 'cpu охладител': return 'cpuCooler';
      case 'захранване': return 'powerSupply';
      default: return '';
    }
  };

  // Get upgrade options for a component
  const getUpgradeOptions = (componentKey: string): any[] => {
    if (!configuration) return [];
    
    switch (componentKey) {
      case 'gpu': return configuration.gpuUpgrades || [];
      case 'cpu': return configuration.cpuUpgrades || [];
      case 'ram': return configuration.ramUpgrades || [];
      case 'motherboard': return configuration.motherboardUpgrades || [];
      case 'ssd': return configuration.ssdUpgrades || [];
      case 'hdd': return configuration.hddUpgrades || [];
      case 'pcCase': return configuration.pcCaseUpgrades || [];
      case 'cpuCooler': return configuration.cpuCoolerUpgrades || [];
      case 'powerSupply': return configuration.powerSupplyUpgrades || [];
      default: return [];
    }
  };

  const handleAddToCart = async () => {
    if (!configuration) return;

    const basePrice = configuration.manualPrice 
      ? parseFloat(configuration.manualPrice?.toString() || '0') || 0
      : parseFloat(configuration.totalPrice?.toString() || '0') || 0;

    const upgradePrice = calculateUpgradePrice();
    const finalPrice = basePrice + upgradePrice;

    // Get PC case image if available (use custom images for used configs)
    let imageUrl = null;
    const baseUrl = config.api.baseUrl;

    // First check if there are custom images (for used configs) - use the first one
    if (configuration.imageUrls && configuration.imageUrls.length > 0) {
      imageUrl = `${baseUrl}${configuration.imageUrls[0]}`;
    }
    // Fallback to old single image field
    else if (configuration.imageUrl) {
      imageUrl = `${baseUrl}${configuration.imageUrl}`;
    }
    // Otherwise try to get PC case image
    else if (configuration.pcCaseId) {
      try {
        imageUrl = await PcConfigService.getPcCaseImageUrl(configuration.pcCaseId, configuration.pcCaseSupplier);
      } catch (error) {
        logger.error('Failed to fetch PC case image for cart:', error);
      }
    }

    // Prepare base components data
    const baseComponents = [];
    if (configuration.pcCaseName && configuration.pcCasePrice) {
      baseComponents.push({ type: 'pcCase' as const, name: configuration.pcCaseName, price: configuration.pcCasePrice });
    }
    if (configuration.cpuName && configuration.cpuPrice) {
      baseComponents.push({ type: 'cpu' as const, name: configuration.cpuName, price: configuration.cpuPrice });
    }
    if (configuration.motherboardName && configuration.motherboardPrice) {
      baseComponents.push({ type: 'motherboard' as const, name: configuration.motherboardName, price: configuration.motherboardPrice });
    }
    if (configuration.cpuCoolerName && configuration.cpuCoolerPrice) {
      baseComponents.push({ type: 'cpuCooler' as const, name: configuration.cpuCoolerName, price: configuration.cpuCoolerPrice });
    }
    if (configuration.ramName && configuration.ramPrice) {
      baseComponents.push({ type: 'ram' as const, name: configuration.ramName, price: configuration.ramPrice });
    }
    if (configuration.ssdName && configuration.ssdPrice) {
      baseComponents.push({ type: 'ssd' as const, name: configuration.ssdName, price: configuration.ssdPrice });
    }
    if (configuration.hddName && configuration.hddPrice) {
      baseComponents.push({ type: 'hdd' as const, name: configuration.hddName, price: configuration.hddPrice });
    }
    if (configuration.gpuName && configuration.gpuPrice) {
      baseComponents.push({ type: 'gpu' as const, name: configuration.gpuName, price: configuration.gpuPrice });
    }
    if (configuration.powerSupplyName && configuration.powerSupplyPrice) {
      baseComponents.push({ type: 'powerSupply' as const, name: configuration.powerSupplyName, price: configuration.powerSupplyPrice });
    }

    // Prepare selected upgrades data
    const selectedUpgradesList = Object.entries(selectedUpgrades).map(([componentType, upgrade]) => ({
      componentType,
      upgradeName: upgrade.name,
      originalPrice: getBaseComponentPrice(componentType),
      upgradePrice: upgrade.price
    }));

    const cartItem = {
      id: configuration.id,
      referenceNumber: `PC-CONFIG-${configuration.id}`,
      name: configuration.name,
      price: finalPrice,
      pricePromo: null,
      quantity: quantity,
      image: imageUrl,
      pcConfigId: configuration.id,
      pcConfigDetails: {
        configName: configuration.name,
        baseComponents,
        selectedUpgrades: selectedUpgradesList,
        basePrice,
        upgradePrice,
        totalPrice: finalPrice
      }
    };

    addToCart(cartItem);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: configuration?.name || 'PC Конфигурация',
          text: `Разгледай тази PC конфигурация: ${configuration?.name}`,
          url: window.location.href
        });
      } catch (error) {
        logger.log('Error sharing:', error);
      }
    } else {
      // Fallback to copying URL to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Линкът е копиран в клипборда');
    }
  };

  if (loading) {
    return (
      <Layout cartItemsCount={cart.items.length} cartTotal={cart.total}>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-500">Зареждане на конфигурация...</p>
        </div>
      </Layout>
    );
  }

  if (error || !configuration) {
    return (
      <Layout cartItemsCount={cart.items.length} cartTotal={cart.total}>
        <div className="text-center py-12">
          <Computer className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">Конфигурацията не е намерена</h3>
          <p className="text-gray-500 mb-6">{error}</p>
          <Link
            to="/konfiguratsii"
            className="inline-flex items-center px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Обратно към конфигурациите
          </Link>
        </div>
      </Layout>
    );
  }

  const components = [
    {
      icon: <Box className="h-6 w-6" />,
      label: 'PC Кутия',
      name: configuration.pcCaseName,
      price: configuration.pcCasePrice
    },
    {
      icon: <Cpu className="h-6 w-6" />,
      label: 'Процесор (CPU)',
      name: configuration.cpuName,
      price: configuration.cpuPrice
    },
    {
      icon: <CircuitBoard className="h-6 w-6" />,
      label: 'Дънна платка',
      name: configuration.motherboardName,
      price: configuration.motherboardPrice
    },
    {
      icon: <Fan className="h-6 w-6" />,
      label: 'CPU Охладител',
      name: configuration.cpuCoolerName,
      price: configuration.cpuCoolerPrice
    },
    {
      icon: <MemoryStick className="h-6 w-6" />,
      label: 'Памет (RAM)',
      name: configuration.ramName,
      price: configuration.ramPrice
    },
    {
      icon: <HardDrive className="h-6 w-6" />,
      label: 'SSD съхранение',
      name: configuration.ssdName,
      price: configuration.ssdPrice
    },
    {
      icon: <HardDrive className="h-6 w-6" />,
      label: 'HDD съхранение',
      name: configuration.hddName,
      price: configuration.hddPrice
    },
    {
      icon: <Monitor className="h-6 w-6" />,
      label: 'Видеокарта (GPU)',
      name: configuration.gpuName,
      price: configuration.gpuPrice
    },
    {
      icon: <Zap className="h-6 w-6" />,
      label: 'Захранване',
      name: configuration.powerSupplyName,
      price: configuration.powerSupplyPrice
    }
  ].filter(component => component.name);

  // Used configs have their characteristics typed into the description - show them as specifications
  const descriptionSpecs = configuration.condition === 'used'
    ? splitDescriptionSpecs(configuration.description)
    : { specs: [], text: configuration.description || '' };
  const specComponents = descriptionSpecs.specs.length > 0
    ? descriptionSpecs.specs.map(spec => ({ icon: getSpecIcon(spec.label), label: spec.label, name: spec.value, price: undefined }))
    : components;

  return (
    <Layout cartItemsCount={cart.items.length} cartTotal={cart.total}>
      <div className="bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Navigation */}
          <div className="mb-8">
            <Link
              to="/konfiguratsii"
              className="inline-flex items-center text-primary-600 hover:text-primary-700 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              <span className="text-lg">Обратно към PC конфигурациите</span>
            </Link>
          </div>

          {/* Main Product Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 xl:gap-16">
            {/* Left Column - Product Image */}
            <div className="space-y-6">
              <PcCaseImage
                pcCaseId={configuration.pcCaseId}
                pcCaseSupplier={configuration.pcCaseSupplier}
                configName={configuration.name}
                customImageUrl={configuration.imageUrl}
                customImageUrls={configuration.imageUrls}
              />
              
              {/* Tags below image */}
              {configuration.tags && configuration.tags.length > 0 && (
                <div className="flex flex-wrap gap-3">
                  {configuration.tags.map((tag, index) => (
                    <span 
                      key={index}
                      className="inline-block px-4 py-2 text-sm bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Right Column - Product Info */}
            <div className="space-y-8">
              {/* Product Header */}
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  {configuration.isFeatured && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                      <Star className="h-4 w-4 mr-1" fill="currentColor" />
                      Препоръчана
                    </span>
                  )}
                  <div className="flex items-center">
                    <button
                      onClick={handleShare}
                      className="flex items-center text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      <Share2 className="h-4 w-4 mr-1" />
                      <span className="text-sm">Сподели</span>
                    </button>
                  </div>
                </div>
                
                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">{configuration.name}</h1>
                
                {/* Description */}
                {descriptionSpecs.text && (
                  <p className="text-xl text-gray-700 leading-relaxed whitespace-pre-line">{descriptionSpecs.text}</p>
                )}
              </div>

              {/* Price Section */}
              <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg">
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="text-5xl font-bold text-green-600 mb-2">
                      €{toEUR((configuration.manualPrice
                        ? parseFloat(configuration.manualPrice?.toString() || '0') || 0
                        : parseFloat(configuration.totalPrice?.toString() || '0') || 0) + calculateUpgradePrice())}
                    </div>
                    <div className="text-gray-600 text-lg">
                      {configuration.condition === 'used'
                        ? (configuration.manualPrice ? 'Крайна цена' : 'Обща цена')
                        : (configuration.manualPrice ? 'Крайна цена с ДДС' : 'Обща цена с ДДС')}
                    </div>
                    
                    {/* Upgrade Price Breakdown */}
                    {calculateUpgradePrice() > 0 && (
                      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="text-sm text-gray-600">
                          Основна цена: €{toEUR(configuration.manualPrice
                            ? parseFloat(configuration.manualPrice?.toString() || '0') || 0
                            : parseFloat(configuration.totalPrice?.toString() || '0') || 0)}
                        </div>
                        <div className="text-sm text-blue-600 font-semibold">
                          Ъпгрейди: +€{toEUR(calculateUpgradePrice())}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Quantity Selector */}
                  <div className="flex items-center justify-center space-x-4">
                    <span className="text-gray-700 font-medium">Количество:</span>
                    <div className="flex items-center border border-gray-300 rounded-lg">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-l-lg transition-colors"
                        disabled={quantity <= 1}
                      >
                        -
                      </button>
                      <span className="px-4 py-2 bg-gray-50 font-semibold min-w-[60px] text-center">
                        {quantity}
                      </span>
                      <button
                        onClick={() => setQuantity(Math.min(10, quantity + 1))}
                        className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-r-lg transition-colors"
                        disabled={quantity >= 10}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {/* Total Price Display */}
                  {quantity > 1 && (
                    <div className="text-center border-t border-gray-200 pt-4">
                      <div className="text-2xl font-bold text-gray-900">
                        Обща стойност: €{toEUR(((configuration.manualPrice
                          ? parseFloat(configuration.manualPrice?.toString() || '0') || 0
                          : parseFloat(configuration.totalPrice?.toString() || '0') || 0) + calculateUpgradePrice()) * quantity)}
                      </div>
                    </div>
                  )}

                  {/* Add to Cart Button */}
                  <button
                    onClick={handleAddToCart}
                    className="w-full bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-bold py-4 px-8 rounded-xl text-xl transition-all duration-300 transform hover:scale-105 hover:shadow-xl flex items-center justify-center space-x-3"
                  >
                    <ShoppingCart className="h-6 w-6" />
                    <span>Добави в кошницата ({quantity})</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Components/Specifications Section */}
          <div className="mt-16">
            <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-lg">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Спецификации</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {specComponents.map((component, index) => {
                  const componentKey = getComponentKey(component.label);
                  const hasUpgrades = configuration && getUpgradeOptions(componentKey).length > 0;
                  const selectedUpgrade = selectedUpgrades[componentKey];
                  const displayName = selectedUpgrade ? selectedUpgrade.name : component.name;
                  
                  return (
                    <div key={index} className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:border-primary-500 transition-all duration-500 hover:shadow-lg group flex flex-col hover:scale-[1.02] hover:z-10 relative">
                      <div className="text-center space-y-4 flex-1">
                        <div className="inline-flex p-4 rounded-full bg-primary-500 text-white group-hover:bg-primary-600 transition-all duration-300 group-hover:scale-110">
                          {component.icon}
                        </div>
                        <div className="flex flex-col flex-1">
                          <div className="text-sm font-medium text-primary-600 uppercase tracking-wider mb-2">
                            {component.label}
                          </div>
                          <div className="text-gray-900 font-semibold text-lg leading-tight mb-3 min-h-[3.5rem] flex items-center justify-center">
                            <div className="line-clamp-2 text-center" title={displayName}>
                              {displayName}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Upgrade Options - Fixed at bottom */}
                      <div className="mt-auto">
                        {hasUpgrades && (
                          <div className="space-y-3 mt-4 pt-4 border-t border-gray-300">
                            <div className="text-xs font-semibold text-primary-600 uppercase tracking-wider mb-2">
                              Ъпгрейд опции
                            </div>
                            <div className="relative">
                              <select 
                                value={selectedUpgrade?.id || ''} 
                                onChange={(e) => {
                                  const upgradeId = e.target.value;
                                  if (upgradeId) {
                                    const upgrade = getUpgradeOptions(componentKey).find(u => String(u.id) === upgradeId);
                                    handleUpgradeSelect(componentKey, upgrade);
                                  } else {
                                    handleUpgradeSelect(componentKey, null);
                                  }
                                }}
                                className="w-full text-sm border-2 border-gray-300 rounded-lg px-3 py-2 bg-white focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 transition-all duration-200 font-medium text-gray-700 hover:border-gray-400 cursor-pointer appearance-none"
                              >
                                <option value="" className="font-medium text-gray-600">Стандартно</option>
                                {getUpgradeOptions(componentKey).map(upgrade => {
                                  const priceDifference = upgrade.price - getBaseComponentPrice(componentKey);
                                  return (
                                    <option key={upgrade.id} value={upgrade.id} className="font-medium">
                                      {upgrade.name}
                                      {priceDifference !== 0 && (
                                        priceDifference > 0
                                          ? ` (+€${priceDifference.toFixed(2)})`
                                          : ` (-€${Math.abs(priceDifference).toFixed(2)})`
                                      )}
                                    </option>
                                  );
                                })}
                              </select>
                              {/* Custom dropdown arrow */}
                              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                <ChevronDown className="h-4 w-4 text-gray-400" />
                              </div>
                            </div>
                            
                            {/* Selected upgrade price highlight */}
                            {selectedUpgrade && (
                              <div className="bg-primary-50 border border-primary-200 rounded-lg p-3">
                                <div className="text-xs text-primary-600 font-semibold mb-1">Избран ъпгрейд:</div>
                                <div className="text-sm text-primary-700 font-medium">{selectedUpgrade.name}</div>
                                <div className="text-sm font-bold text-green-600 mt-1">
                                  {(selectedUpgrade.price - getBaseComponentPrice(componentKey)) > 0 ? '+' : ''}€{toEUR(selectedUpgrade.price - getBaseComponentPrice(componentKey))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PCConfigDetailPage;